import React, { useState, useEffect } from 'react';
import { 
    Settings, Shield, CreditCard, Bell, Globe, 
    Save, CheckCircle2, Lock, Building, FileCheck,
    PhoneCall, Video, ShieldAlert, UserCheck
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isRTL } from '../utils/translations';

export default function SellerSettings() {
    const rtl = isRTL();
    const { user } = useAuth();
    const [saving, setSaving] = useState(false);
    const [savedNotice, setSavedNotice] = useState(false);

    const [settings, setSettings] = useState({
        payoutBankName: 'Al Rajhi Bank',
        payoutIban: 'SA4480000123456789012345',
        payoutBeneficiary: user?.fullName || 'LUXVONO Partner LLC',
        defaultCurrency: 'SAR',
        notifyOnNewOrder: true,
        notifyOnModerationStatus: true,
        notifyWeeklyReport: true,
        commercialRegister: '1010789012',
        vatTaxNumber: '310123456700003',
        // Call Permissions (Section 23)
        allowIncomingCalls: true,
        callPolicy: 'VOICE_AND_VIDEO', // 'VOICE_AND_VIDEO', 'VOICE_ONLY', 'VIDEO_ONLY', 'FOLLOWERS_ONLY', 'CUSTOMERS_WITH_ORDERS_ONLY', 'NOBODY'
        // Safety & Moderation Preferences
        autoBlockReportedSpam: true,
        filterLiveProfanity: true,
        defaultMuteDuration: '5m' // '5m', '30m', 'until_end'
    });

    useEffect(() => {
        try {
            const stored = localStorage.getItem('luxvono_seller_account_settings');
            if (stored) {
                const parsed = JSON.parse(stored);
                setSettings(prev => ({ ...prev, ...parsed }));
            }
        } catch (_) {}
    }, []);

    const handleSave = (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            localStorage.setItem('luxvono_seller_account_settings', JSON.stringify(settings));
            setSavedNotice(true);
            setTimeout(() => setSavedNotice(false), 4000);
        } catch (_) {}
        setSaving(false);
    };

    return (
        <div style={{ maxWidth: 840, marginInline: 'auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ margin: '0 0 6px 0', fontSize: 22, fontWeight: 800, color: 'var(--seller-text, #111827)' }}>
                        {rtl ? 'إعدادات الحساب التجاري والتسويات' : 'Merchant Account & Settlement Settings'}
                    </h1>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--seller-muted, #64748b)' }}>
                        {rtl 
                            ? 'إدارة الحسابات البنكية لاستلام مستحقات المبيعات، العملات المفضلة، والتنبيهات.' 
                            : 'Configure IBAN payout credentials, preferred operational currencies, and alerts.'}
                    </p>
                </div>

                <button 
                    onClick={handleSave}
                    disabled={saving}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FE2C55', color: '#ffffff', padding: '10px 24px', borderRadius: 10, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(254, 44, 85, 0.35)', transition: 'all 0.2s ease' }}
                >
                    <Save size={16} />
                    <span>{saving ? (rtl ? 'جارٍ الحفظ...' : 'Saving...') : (rtl ? 'حفظ الإعدادات' : 'Save Settings')}</span>
                </button>
            </div>

            {/* Saved Banner */}
            {savedNotice && (
                <div style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 10, padding: '12px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, color: '#059669', fontSize: 13, fontWeight: 600 }}>
                    <CheckCircle2 size={18} />
                    <span>{rtl ? 'تم حفظ كافة الإعدادات والبيانات البنكية بنجاح!' : 'Merchant settings and bank details updated successfully!'}</span>
                </div>
            )}

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* Bank / Payout Section */}
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, borderBottom: '1px solid var(--seller-border, #e5e7eb)', paddingBottom: 12 }}>
                        <CreditCard size={20} color="#FE2C55" />
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--seller-text, #111827)' }}>
                            {rtl ? 'بيانات التحويل البنكي والتسويات المالية' : 'Direct Bank Payout & Settlement Information'}
                        </h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                                {rtl ? 'اسم البنك المستلم' : 'Bank Name'}
                            </label>
                            <input 
                                type="text" 
                                value={settings.payoutBankName} 
                                onChange={e => setSettings({ ...settings, payoutBankName: e.target.value })} 
                                style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                                {rtl ? 'اسم المستفيد (مطابق للسجل التجاري)' : 'Beneficiary Account Name'}
                            </label>
                            <input 
                                type="text" 
                                value={settings.payoutBeneficiary} 
                                onChange={e => setSettings({ ...settings, payoutBeneficiary: e.target.value })} 
                                style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                            />
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                                {rtl ? 'رقم الآيبان الدولي (IBAN)' : 'IBAN Account Number'}
                            </label>
                            <input 
                                type="text" 
                                value={settings.payoutIban} 
                                onChange={e => setSettings({ ...settings, payoutIban: e.target.value })} 
                                dir="ltr"
                                style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: '#FE2C55', fontWeight: 700, fontSize: 14, letterSpacing: '1px', boxSizing: 'border-box' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Verification & Compliance */}
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, borderBottom: '1px solid var(--seller-border, #e5e7eb)', paddingBottom: 12 }}>
                        <Building size={20} color="#111827" />
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--seller-text, #111827)' }}>
                            {rtl ? 'الامتثال التجاري والضريبي' : 'Commercial Registration & Tax Compliance'}
                        </h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                                {rtl ? 'رقم السجل التجاري (CR)' : 'Commercial Registration (CR)'}
                            </label>
                            <input 
                                type="text" 
                                value={settings.commercialRegister} 
                                onChange={e => setSettings({ ...settings, commercialRegister: e.target.value })} 
                                style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 6 }}>
                                {rtl ? 'الرقم الضريبي (VAT / Tax ID)' : 'VAT / Tax Identification'}
                            </label>
                            <input 
                                type="text" 
                                value={settings.vatTaxNumber} 
                                onChange={e => setSettings({ ...settings, vatTaxNumber: e.target.value })} 
                                style={{ width: '100%', padding: '10px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Preferences & Notifications */}
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, borderBottom: '1px solid var(--seller-border, #e5e7eb)', paddingBottom: 12 }}>
                        <Bell size={20} color="#10b981" />
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--seller-text, #111827)' }}>
                            {rtl ? 'تفضيلات الإشعارات والعملات' : 'Preferences & Alerts'}
                        </h3>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--seller-border, #e5e7eb)' }}>
                            <div>
                                <strong style={{ color: 'var(--seller-text, #111827)', fontSize: 13, display: 'block' }}>
                                    {rtl ? 'تنبيه فوري عند استلام طلب شراء جديد' : 'New Order Alerts'}
                                </strong>
                                <span style={{ color: 'var(--seller-muted, #64748b)', fontSize: 12 }}>
                                    {rtl ? 'إرسال بريد وتنبيه لوحة التحكم فور إتمام الدفع من العميل.' : 'Receive instant notification when order is paid.'}
                                </span>
                            </div>
                            <input 
                                type="checkbox" 
                                checked={settings.notifyOnNewOrder} 
                                onChange={e => setSettings({ ...settings, notifyOnNewOrder: e.target.checked })}
                                style={{ width: 18, height: 18, accentColor: '#FE2C55' }}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
                            <div>
                                <strong style={{ color: 'var(--seller-text, #111827)', fontSize: 13, display: 'block' }}>
                                    {rtl ? 'إشعارات قبول أو تعديل المنتجات' : 'Moderation Status Alerts'}
                                </strong>
                                <span style={{ color: 'var(--seller-muted, #64748b)', fontSize: 12 }}>
                                    {rtl ? 'إشعارك فور اعتماد المنتج أو في حال طلب الإدارة تعديل الصور.' : 'Get notified when listings are approved or flagged for edits.'}
                                </span>
                            </div>
                            <input 
                                type="checkbox" 
                                checked={settings.notifyOnModerationStatus} 
                                onChange={e => setSettings({ ...settings, notifyOnModerationStatus: e.target.checked })}
                                style={{ width: 18, height: 18, accentColor: '#FE2C55' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Call Permissions Section (Section 21, 22, 23) */}
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, borderBottom: '1px solid var(--seller-border, #e5e7eb)', paddingBottom: 12 }}>
                        <PhoneCall size={20} color="#FE2C55" />
                        <div>
                            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--seller-text, #111827)' }}>
                                {rtl ? 'أذونات المكالمات الصوتية والمرئية (WebRTC)' : 'Customer Call Permissions (WebRTC Consultation)'}
                            </h3>
                            <span style={{ fontSize: 12, color: 'var(--seller-muted, #64748b)' }}>
                                {rtl ? 'تحديد من يُسمح له ببدء مكالمة استشارة مباشرة معك عبر المنصة.' : 'Define who can initiate real-time audio/video consultations with your store.'}
                            </span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--seller-border, #f1f3f5)' }}>
                            <div>
                                <strong style={{ color: 'var(--seller-text, #111827)', fontSize: 13, display: 'block' }}>
                                    {rtl ? 'تفعيل استقبال المكالمات المباشرة' : 'Allow Incoming Consultation Calls'}
                                </strong>
                                <span style={{ color: 'var(--seller-muted, #64748b)', fontSize: 12 }}>
                                    {rtl ? 'عند التعطيل، لن يتمكن أي عميل من الاتصال بك مباشرة.' : 'When disabled, call action buttons on your profile will be deactivated.'}
                                </span>
                            </div>
                            <input 
                                type="checkbox" 
                                checked={settings.allowIncomingCalls} 
                                onChange={e => setSettings({ ...settings, allowIncomingCalls: e.target.checked })} 
                                style={{ width: 18, height: 18, accentColor: '#FE2C55' }}
                            />
                        </div>

                        {settings.allowIncomingCalls && (
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 8 }}>
                                    {rtl ? 'نطاق السماح بالمكالمات' : 'Who is Permitted to Call'}
                                </label>
                                <select 
                                    value={settings.callPolicy} 
                                    onChange={e => setSettings({ ...settings, callPolicy: e.target.value })}
                                    style={{ width: '100%', padding: '11px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                                >
                                    <option value="VOICE_AND_VIDEO">{rtl ? 'صوتي وفيديو (كافة العملاء الموثقين)' : 'Voice & Video (All Verified Customers)'}</option>
                                    <option value="VOICE_ONLY">{rtl ? 'صوتي فقط (كافة العملاء)' : 'Voice Only (All Customers)'}</option>
                                    <option value="VIDEO_ONLY">{rtl ? 'فيديو فقط (معاينة المنتجات)' : 'Video Only (Product Demo)'}</option>
                                    <option value="FOLLOWERS_ONLY">{rtl ? 'المتابعون للمتجر فقط' : 'Store Followers Only'}</option>
                                    <option value="CUSTOMERS_WITH_ORDERS_ONLY">{rtl ? 'العملاء الذين لديهم طلبات شراء مؤكدة فقط' : 'Customers with Confirmed Orders Only'}</option>
                                    <option value="NOBODY">{rtl ? 'تعطيل استقبال المكالمات حالياً' : 'Disabled (Nobody)'}</option>
                                </select>
                            </div>
                        )}

                        <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '12px 14px', fontSize: 12, color: '#9f1239', lineHeight: 1.5 }}>
                            <Shield size={14} style={{ display: 'inline', verticalAlign: '-2px', marginInlineEnd: 6, color: '#FE2C55' }} />
                            {rtl 
                                ? 'خصوصية مشددة: تتم جميع المكالمات عبر تقنية WebRTC المشفرة وخوادم Coturn الوسيطة الخاصة بمنصة LUXVONO. لا يتم كشف رقم هاتفك أو بريدك للعملاء إطلاقاً.' 
                                : 'Privacy Guaranteed: All calls run over encrypted LUXVONO WebRTC media relays via Coturn TURN. Your phone number and email are strictly hidden.'}
                        </div>
                    </div>
                </div>

                {/* Moderation & Safety Preferences */}
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, borderBottom: '1px solid var(--seller-border, #e5e7eb)', paddingBottom: 12 }}>
                        <ShieldAlert size={20} color="#f59e0b" />
                        <div>
                            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--seller-text, #111827)' }}>
                                {rtl ? 'أمان المحادثات والبث المباشر' : 'Live Commerce & Chat Safety Controls'}
                            </h3>
                            <span style={{ fontSize: 12, color: 'var(--seller-muted, #64748b)' }}>
                                {rtl ? 'إعدادات الإشراف والحظر وتصفية الدردشة أثناء البث المباشر.' : 'Moderation rules, automated filters, and default mute policies.'}
                            </span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--seller-border, #f1f3f5)' }}>
                            <div>
                                <strong style={{ color: 'var(--seller-text, #111827)', fontSize: 13, display: 'block' }}>
                                    {rtl ? 'تصفية الكلمات غير اللائقة تلقائياً' : 'Automatic Profanity & Abuse Filter'}
                                </strong>
                                <span style={{ color: 'var(--seller-muted, #64748b)', fontSize: 12 }}>
                                    {rtl ? 'حجب الرسائل غير اللائقة تلقائياً في شات البث المباشر ورسائل المتجر.' : 'Block offensive text automatically in live stream chat and direct inquiries.'}
                                </span>
                            </div>
                            <input 
                                type="checkbox" 
                                checked={settings.filterLiveProfanity} 
                                onChange={e => setSettings({ ...settings, filterLiveProfanity: e.target.checked })} 
                                style={{ width: 18, height: 18, accentColor: '#f59e0b' }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--seller-text, #374151)', marginBottom: 8 }}>
                                {rtl ? 'المدة الافتراضية لكتم المستخدمين أثناء البث' : 'Default Live Chat Mute Duration'}
                            </label>
                            <select 
                                value={settings.defaultMuteDuration} 
                                onChange={e => setSettings({ ...settings, defaultMuteDuration: e.target.value })}
                                style={{ width: '100%', padding: '11px 14px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                            >
                                <option value="5m">{rtl ? 'كتم لمدة 5 دقائق' : 'Mute 5 Minutes'}</option>
                                <option value="30m">{rtl ? 'كتم لمدة 30 دقيقة' : 'Mute 30 Minutes'}</option>
                                <option value="until_end">{rtl ? 'كتم حتى نهاية البث المباشر' : 'Mute Until Live Ends'}</option>
                            </select>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
